import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Calendar16 from '@carbon/icons-react/es/calendar/16';
import { Space } from 'antd';
import moment from 'moment';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { AccountStatus, WarningType } from 'api/account';
import { ErrorCodes } from 'api/errors/types';
import { SupportedPlansType } from 'api/billing/types';
import { PermissionSetType } from 'api/models';
import { Alert } from 'components/atoms/alert';
import { Caption01, Text02 } from 'components/styles';
import { useUserPermissionSetType } from 'hooks/permissions';
import { useAccount } from 'hooks/use-account';
import { useIsAccountInEnterpriseBillingType } from 'hooks/use-enterprise';
import { useAccountDataQuery } from 'queries/account';
const AnnouncementBanner = styled(Alert)`
  padding: var(--spacing-03) var(--spacing-06);
  .ant-alert-message {
    width: auto;
    text-align: center;
    ${Caption01};
  }
`;
const Container = styled.div`
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: var(--zindex-modal);
  display: flex;
  justify-content: center;
  /* Full-width fixed shell must not steal clicks from header actions behind it. */
  pointer-events: none;

  ${AnnouncementBanner} {
    width: 100%;
    pointer-events: auto;
  }
`;
const ContainerV2 = styled.div`
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: var(--zindex-modal);
  display: flex;
  justify-content: center;
  width: calc(100vw - 32px);
  max-width: calc(100vw - 32px);
  /* Full-width fixed shell must not steal clicks from header actions behind it. */
  pointer-events: none;

  ${AnnouncementBanner} {
    width: auto;
    max-width: 100%;
    pointer-events: auto;
  }
`;
const BuyLink = styled.span`
  margin-left: var(--spacing-02);
  text-decoration: underline;
  cursor: pointer;
`;
const Main = styled.div``;
const DemoLink = styled.a`
  display: flex;
  align-items: center;
  ${Text02};
  > svg {
    margin-right: var(--spacing-02);
  }
`;
/**
 * bannerV2-only: prefer a single aligned row, but wrap within the
 * viewport so long Nitro copy + CTAs never overflow horizontally.
 */ const BannerV2Content = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  column-gap: 96px;
  row-gap: var(--spacing-03);
  max-width: 100%;
`;
const MainV2 = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: var(--spacing-02);
  min-width: 0;
  max-width: 100%;
  flex: 1 1 16rem;

  ${BuyLink} {
    margin-left: 0;
    white-space: nowrap;
    flex-shrink: 0;
  }
`;
const MessageText = styled.span`
  white-space: normal;
  text-align: center;
`;
const DemoLinkV2 = styled.a`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  white-space: nowrap;
  ${Caption01};
  > svg {
    margin-right: var(--spacing-02);
    flex-shrink: 0;
  }
`;
export const TrialExpiryBanner = ({ onBuy, onBookDemo })=>{
    var _accountData_billingMeta_subscription_plan, _accountData_billingMeta_subscription, _accountData_billingMeta, _accountData_billingMeta_subscription_plan1, _accountData_billingMeta_subscription1, _accountData_billingMeta1, _accountData_billingMeta_subscription_plan2, _accountData_billingMeta_subscription2, _accountData_billingMeta2, _accountData_accountMeta_features, _accountData_accountMeta;
    const { expiryDate, status, accountWarnings } = useAccount();
    const currentUserPermissionSetType = useUserPermissionSetType();
    const { data: accountData } = useAccountDataQuery();
    const isAccountInEnterpriseBillingType = useIsAccountInEnterpriseBillingType();
    const planType = (accountData === null || accountData === void 0 ? void 0 : (_accountData_billingMeta = accountData.billingMeta) === null || _accountData_billingMeta === void 0 ? void 0 : (_accountData_billingMeta_subscription = _accountData_billingMeta.subscription) === null || _accountData_billingMeta_subscription === void 0 ? void 0 : (_accountData_billingMeta_subscription_plan = _accountData_billingMeta_subscription.plan) === null || _accountData_billingMeta_subscription_plan === void 0 ? void 0 : _accountData_billingMeta_subscription_plan.planType) === SupportedPlansType.Premium.toUpperCase() || (accountData === null || accountData === void 0 ? void 0 : (_accountData_billingMeta1 = accountData.billingMeta) === null || _accountData_billingMeta1 === void 0 ? void 0 : (_accountData_billingMeta_subscription1 = _accountData_billingMeta1.subscription) === null || _accountData_billingMeta_subscription1 === void 0 ? void 0 : (_accountData_billingMeta_subscription_plan1 = _accountData_billingMeta_subscription1.plan) === null || _accountData_billingMeta_subscription_plan1 === void 0 ? void 0 : _accountData_billingMeta_subscription_plan1.planType) === SupportedPlansType['Premium-V2'].toUpperCase() ? SupportedPlansType.Premium : isAccountInEnterpriseBillingType ? SupportedPlansType.Enterprise : accountData === null || accountData === void 0 ? void 0 : (_accountData_billingMeta2 = accountData.billingMeta) === null || _accountData_billingMeta2 === void 0 ? void 0 : (_accountData_billingMeta_subscription2 = _accountData_billingMeta2.subscription) === null || _accountData_billingMeta_subscription2 === void 0 ? void 0 : (_accountData_billingMeta_subscription_plan2 = _accountData_billingMeta_subscription2.plan) === null || _accountData_billingMeta_subscription_plan2 === void 0 ? void 0 : _accountData_billingMeta_subscription_plan2.planType.toUpperCase();
    const { expiringIn, isExpiringSoon, isAlreadyExpired } = useMemo(()=>{
        const today = moment().endOf('day');
        const expiry = moment(expiryDate).endOf('day');
        const result = today.diff(expiry, 'days');
        const _isAlreadyExpired = result > 0;
        return {
            expiringIn: _isAlreadyExpired ? 0 : Math.abs(result),
            isAlreadyExpired: _isAlreadyExpired,
            isExpiringSoon: Math.abs(result) <= 7
        };
    }, [
        expiryDate
    ]);
    const isNitroAccount = useMemo(()=>{
        var _accountData_accountMeta_features, _accountData_accountMeta;
        return Boolean(accountData === null || accountData === void 0 ? void 0 : (_accountData_accountMeta = accountData.accountMeta) === null || _accountData_accountMeta === void 0 ? void 0 : (_accountData_accountMeta_features = _accountData_accountMeta.features) === null || _accountData_accountMeta_features === void 0 ? void 0 : _accountData_accountMeta_features.scarlet);
    }, [
        accountData
    ]);
    const isBannerV2Enabled = Boolean(accountData === null || accountData === void 0 ? void 0 : (_accountData_accountMeta = accountData.accountMeta) === null || _accountData_accountMeta === void 0 ? void 0 : (_accountData_accountMeta_features = _accountData_accountMeta.features) === null || _accountData_accountMeta_features === void 0 ? void 0 : _accountData_accountMeta_features.bannerV2);
    const nitroMessageKey = useMemo(()=>{
        var _ref;
        if (!isBannerV2Enabled || !isNitroAccount) {
            return null;
        }
        const isOutOfCredits = (_ref = accountWarnings === null || accountWarnings === void 0 ? void 0 : accountWarnings.some((w)=>w.warningType === WarningType.Nitro && w.errorCode === ErrorCodes.NitroOutOfCredits)) !== null && _ref !== void 0 ? _ref : false;
        // Trial Nitro messaging is only for NITRO_OUT_OF_CREDITS.
        if (!isOutOfCredits) {
            return null;
        }
        if (isAlreadyExpired) {
            return 'nitro.banner.trial_expired_out_of_credits';
        }
        return isExpiringSoon ? 'nitro.banner.trial_ending_out_of_credits' : 'nitro.banner.out_of_credits';
    }, [
        isBannerV2Enabled,
        isNitroAccount,
        isAlreadyExpired,
        isExpiringSoon,
        accountWarnings
    ]);
    const showBuyLink = !(isAlreadyExpired && currentUserPermissionSetType === PermissionSetType.AccountAdmin);
    if (status !== AccountStatus.Trial) {
        return null;
    }
    // ── bannerV2 OFF: original layout (no visual regression) ─────────────────
    if (!isBannerV2Enabled) {
        return /*#__PURE__*/ _jsx(Container, {
            id: "account-expiry-banner",
            children: /*#__PURE__*/ _jsx(AnnouncementBanner, {
                type: isExpiringSoon ? 'warning' : 'info',
                closable: false,
                message: /*#__PURE__*/ _jsxs(Space, {
                    size: 96,
                    children: [
                        /*#__PURE__*/ _jsxs(Main, {
                            children: [
                                isAlreadyExpired ? /*#__PURE__*/ _jsx(FormattedMessage, {
                                    id: "account.trial.expired.message",
                                    values: {
                                        planType
                                    }
                                }) : /*#__PURE__*/ _jsx(FormattedMessage, {
                                    id: "account.trial.expiry.message",
                                    values: {
                                        days: expiringIn,
                                        planType
                                    }
                                }),
                                showBuyLink ? /*#__PURE__*/ _jsx(BuyLink, {
                                    onClick: onBuy,
                                    "data-cy": "trial-expiry-banner.buy-link",
                                    children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                        id: "account.trial.buy"
                                    })
                                }) : null
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(DemoLink, {
                            onClick: onBookDemo,
                            target: "_blank",
                            "data-cy": "trial-expiry-banner.book-demo",
                            children: [
                                /*#__PURE__*/ _jsx(Calendar16, {}),
                                /*#__PURE__*/ _jsx(FormattedMessage, {
                                    id: "account.trial.book_demo"
                                })
                            ]
                        })
                    ]
                })
            })
        });
    }
    // ── bannerV2 ON: single-line Nitro-aware layout ──────────────────────────
    return /*#__PURE__*/ _jsx(ContainerV2, {
        id: "account-expiry-banner",
        children: /*#__PURE__*/ _jsx(AnnouncementBanner, {
            type: nitroMessageKey ? 'error' : isExpiringSoon ? 'warning' : 'info',
            closable: false,
            message: /*#__PURE__*/ _jsxs(BannerV2Content, {
                children: [
                    /*#__PURE__*/ _jsxs(MainV2, {
                        "data-cy": nitroMessageKey ? 'trial-expiry-banner.nitro-message' : undefined,
                        children: [
                            /*#__PURE__*/ _jsx(MessageText, {
                                children: nitroMessageKey ? /*#__PURE__*/ _jsx(FormattedMessage, {
                                    id: nitroMessageKey
                                }) : isAlreadyExpired ? /*#__PURE__*/ _jsx(FormattedMessage, {
                                    id: "account.trial.expired.message",
                                    values: {
                                        planType
                                    }
                                }) : /*#__PURE__*/ _jsx(FormattedMessage, {
                                    id: "account.trial.expiry.message",
                                    values: {
                                        days: expiringIn,
                                        planType
                                    }
                                })
                            }),
                            showBuyLink ? /*#__PURE__*/ _jsx(BuyLink, {
                                onClick: onBuy,
                                "data-cy": "trial-expiry-banner.buy-link",
                                children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                    id: "account.trial.buy"
                                })
                            }) : null
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(DemoLinkV2, {
                        onClick: onBookDemo,
                        target: "_blank",
                        "data-cy": "trial-expiry-banner.book-demo",
                        children: [
                            /*#__PURE__*/ _jsx(Calendar16, {}),
                            /*#__PURE__*/ _jsx(FormattedMessage, {
                                id: "account.trial.book_demo"
                            })
                        ]
                    })
                ]
            })
        })
    });
};
export const SubscriptionRenewalFailedBanner = ()=>{
    const { accountWarnings } = useAccount();
    const hasWarning = useMemo(()=>accountWarnings === null || accountWarnings === void 0 ? void 0 : accountWarnings.find(({ warningType, errorCode })=>warningType === WarningType.Billing && errorCode === ErrorCodes.PaymentFailed), [
        accountWarnings
    ]);
    if (!hasWarning) {
        return /*#__PURE__*/ _jsx(_Fragment, {});
    }
    return /*#__PURE__*/ _jsx(Container, {
        id: "account-expiry-banner",
        children: /*#__PURE__*/ _jsx(AnnouncementBanner, {
            type: "error",
            closable: false,
            message: /*#__PURE__*/ _jsx(Space, {
                size: 96,
                children: /*#__PURE__*/ _jsx(Main, {
                    children: /*#__PURE__*/ _jsx(FormattedMessage, {
                        id: "billing.subscription.renewal.failed.banner.message",
                        values: {
                            b: (chunks)=>/*#__PURE__*/ _jsx("b", {
                                    children: chunks
                                })
                        }
                    })
                })
            })
        })
    });
};
